import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { Upload, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const MAX_FILES = 3;
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Screenshot upload component with clipboard paste support.
 *
 * @param {Object} props
 * @param {File[]} props.files - Current file list
 * @param {Function} props.onChange - Callback when files change
 * @param {boolean} props.disabled - Disable interactions
 */
const ScreenshotUpload = ({ files = [], onChange, disabled = false }) => {
    const addFiles = useCallback(
        (newFiles) => {
            const current = files || [];
            const remaining = MAX_FILES - current.length;
            if (remaining <= 0) {
                message.warning(`Maximum ${MAX_FILES} screenshots allowed.`);
                return;
            }

            const valid = [];
            for (const file of newFiles.slice(0, remaining)) {
                if (!ACCEPTED_TYPES.includes(file.type)) {
                    message.error(`${file.name}: Only image files are accepted.`);
                    continue;
                }
                if (file.size > MAX_SIZE_BYTES) {
                    message.error(`${file.name}: File exceeds ${MAX_SIZE_MB}MB limit.`);
                    continue;
                }
                valid.push(file);
            }

            if (valid.length > 0) {
                onChange([...current, ...valid]);
            }
        },
        [files, onChange]
    );

    // Clipboard paste handler
    useEffect(() => {
        if (disabled) return;

        const handlePaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            const imageFiles = [];
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.startsWith("image/")) {
                    const blob = items[i].getAsFile();
                    if (blob) {
                        // Create a proper File from the blob with a meaningful name
                        const file = new File(
                            [blob],
                            `clipboard-${Date.now()}.png`,
                            { type: blob.type }
                        );
                        imageFiles.push(file);
                    }
                }
            }

            if (imageFiles.length > 0) {
                e.preventDefault();
                addFiles(imageFiles);
                message.success("Screenshot pasted from clipboard");
            }
        };

        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, [disabled, addFiles]);

    const handleRemove = (index) => {
        const updated = [...files];
        updated.splice(index, 1);
        onChange(updated);
    };

    // Create blob URLs for thumbnails and revoke old ones to prevent memory leaks
    const prevUrlsRef = useRef([]);
    const previewUrls = useMemo(() => {
        // Revoke previous URLs
        prevUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        const urls = files.map((file) => URL.createObjectURL(file));
        prevUrlsRef.current = urls;
        return urls;
    }, [files]);

    // Cleanup all URLs on unmount
    useEffect(() => {
        return () => {
            prevUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        };
    }, []);

    const handleBeforeUpload = (file) => {
        addFiles([file]);
        return false; // Prevent auto upload — we handle it manually on submit
    };

    return (
        <div className="screenshot-upload">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {files.map((file, index) => (
                    <div
                        key={index}
                        style={{
                            position: "relative",
                            width: 104,
                            height: 104,
                            border: "1px solid #d9d9d9",
                            borderRadius: 8,
                            overflow: "hidden",
                        }}
                    >
                        <img
                            src={previewUrls[index]}
                            alt={file.name}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                        <div
                            onClick={() => handleRemove(index)}
                            style={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                background: "rgba(0,0,0,0.5)",
                                color: "#fff",
                                borderRadius: "50%",
                                width: 20,
                                height: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                fontSize: 10,
                            }}
                        >
                            <DeleteOutlined />
                        </div>
                    </div>
                ))}

                {files.length < MAX_FILES && (
                    <Upload
                        beforeUpload={handleBeforeUpload}
                        showUploadList={false}
                        accept="image/*"
                        disabled={disabled}
                    >
                        <div
                            style={{
                                width: 104,
                                height: 104,
                                border: "1px dashed #d9d9d9",
                                borderRadius: 8,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: disabled ? "not-allowed" : "pointer",
                                color: "#999",
                                fontSize: 12,
                                gap: 4,
                            }}
                        >
                            <PlusOutlined style={{ fontSize: 20 }} />
                            <span>Upload</span>
                        </div>
                    </Upload>
                )}
            </div>
            <div
                style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: "#999",
                }}
            >
                Paste from clipboard or click to upload (max {MAX_FILES}, {MAX_SIZE_MB}MB each)
            </div>
        </div>
    );
};

export default ScreenshotUpload;
